import { Money, moneyAdd } from "@redotech/money";
import { Currency, isCurrency } from "@redotech/money/currencies";
import {
  DiscountAllocation,
  DiscountApplication,
} from "@redotech/redo-shopify-order-common/shopify-order";

interface DiscountAllocationCarrier {
  discount_allocations?: DiscountAllocation[] | null;
  [key: string]: unknown; // Needed to appease TypeScript. Why is our Order model so dynamically typed :(
}

const DEFAULT_CURRENCY = Currency.USD;

// Shown when a discount carries no merchant-set code or title.
const DISCOUNT_TYPE_LABELS: Record<DiscountApplication["type"], string> = {
  automatic: "Automatic discount",
  manual: "Manual discount",
  script: "Script discount",
  discount_code: "Discount code",
};

export interface OrderDiscountLine {
  label: string;
  // Positive amount this application took off the order, in presentment currency.
  amount: Money;
}

/**
 * One display line per Shopify discount application, the way the Shopify admin
 * shows them: labeled by code (or automatic-discount title) and summing every
 * line-item and shipping allocation that points at the application.
 */
export function getOrderDiscountLines(order: {
  discount_applications?: DiscountApplication[] | null;
  line_items?: readonly DiscountAllocationCarrier[] | null;
  shipping_lines?: readonly DiscountAllocationCarrier[] | null;
  presentment_currency?: string | null;
  currency?: string | null;
}): OrderDiscountLine[] {
  const currency = ((): Currency => {
    const code = order.presentment_currency ?? order.currency;
    return code && isCurrency(code) ? code : DEFAULT_CURRENCY;
  })();

  const allocations = [
    ...(order.line_items ?? []).flatMap(
      (item) => item.discount_allocations ?? [],
    ),
    ...(order.shipping_lines ?? []).flatMap(
      (line) => line.discount_allocations ?? [],
    ),
  ];

  return (order.discount_applications ?? []).flatMap((application, index) => {
    const amount = allocations
      .filter((allocation) => allocation.discount_application_index === index)
      .reduce<Money>(
        (sum, allocation) =>
          moneyAdd(sum, {
            amount: parseFloat(
              allocation.amount_set?.presentment_money?.amount ??
                allocation.amount,
            ),
            currency,
          }),
        { amount: 0, currency },
      );
    if (amount.amount <= 0) {
      return [];
    }
    return [{ label: discountLabel(application), amount }];
  });
}

function discountLabel(application: DiscountApplication): string {
  return (
    application.code ??
    application.title ??
    DISCOUNT_TYPE_LABELS[application.type]
  );
}
